import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Pin } from 'lucide-react';
import { changelog, ChangelogCategory } from '../data/changelog';
import { cn } from '../utils/cn';
import { useKeycloak } from '../contexts/KeycloakContext';
import { listAnnouncements, SupportAnnouncement } from '../services/avisoService';

// ── Category metadata ──────────────────────────────────────────────────────────

type Filter = 'todas' | ChangelogCategory;

const CAT: Record<string, {
  label: string;
  borderDark: string;
  borderLight: string;
  textDark: string;
  textLight: string;
  filterActive: string;
}> = {
  novidade: {
    label: 'Novidade',
    borderDark: 'border-l-emerald-500',
    borderLight: 'border-l-emerald-600',
    textDark: 'text-emerald-400',
    textLight: 'text-emerald-700',
    filterActive: 'bg-emerald-600 text-white border-emerald-600',
  },
  melhoria: {
    label: 'Melhoria',
    borderDark: 'border-l-blue-500',
    borderLight: 'border-l-blue-600',
    textDark: 'text-blue-400',
    textLight: 'text-blue-700',
    filterActive: 'bg-blue-600 text-white border-blue-600',
  },
  correcao: {
    label: 'Correção',
    borderDark: 'border-l-amber-500',
    borderLight: 'border-l-amber-600',
    textDark: 'text-amber-400',
    textLight: 'text-amber-700',
    filterActive: 'bg-amber-600 text-white border-amber-600',
  },
  seguranca: {
    label: 'Segurança',
    borderDark: 'border-l-violet-500',
    borderLight: 'border-l-violet-600',
    textDark: 'text-violet-400',
    textLight: 'text-violet-700',
    filterActive: 'bg-violet-600 text-white border-violet-600',
  },
  aviso: {
    label: 'Aviso',
    borderDark: 'border-l-orange-500',
    borderLight: 'border-l-orange-500',
    textDark: 'text-orange-400',
    textLight: 'text-orange-600',
    filterActive: 'bg-orange-600 text-white border-orange-600',
  },
  lembrete: {
    label: 'Lembrete',
    borderDark: 'border-l-pink-500',
    borderLight: 'border-l-pink-500',
    textDark: 'text-pink-400',
    textLight: 'text-pink-600',
    filterActive: 'bg-pink-600 text-white border-pink-600',
  },
};

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'todas',     label: 'Todas'     },
  { key: 'novidade',  label: 'Novidade'  },
  { key: 'melhoria',  label: 'Melhoria'  },
  { key: 'correcao',  label: 'Correção'  },
  { key: 'seguranca', label: 'Segurança' },
];

function fmtLong(iso: string) {
  return new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR', {
    day: '2-digit', month: 'long', year: 'numeric',
  });
}

function fmtShort(iso: string) {
  return new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR', {
    month: 'short', year: 'numeric',
  });
}

// ── Component ──────────────────────────────────────────────────────────────────

export interface ChangelogViewerProps {
  lightTheme?: boolean;
  onBack?: () => void;
}

const ChangelogViewer: React.FC<ChangelogViewerProps> = ({ lightTheme, onBack }) => {
  const [selectedVersion, setSelectedVersion] = useState(changelog[0].version);
  const [filter, setFilter] = useState<Filter>('todas');
  const [visible, setVisible] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [announcements, setAnnouncements] = useState<SupportAnnouncement[]>([]);
  const { token } = useKeycloak();

  useEffect(() => {
    if (!token) return;
    listAnnouncements(token).then(setAnnouncements).catch(() => {});
  }, [token]);

  const release = changelog.find(r => r.version === selectedVersion) ?? changelog[0];
  const entries = filter === 'todas'
    ? release.entries
    : release.entries.filter(e => e.category === filter);

  const countFor = (f: Filter) =>
    f === 'todas'
      ? release.entries.length
      : release.entries.filter(e => e.category === f).length;

  const switchVersion = (version: string) => {
    if (version === selectedVersion) return;
    setVisible(false);
    setTimeout(() => {
      setSelectedVersion(version);
      setFilter('todas');
      setVisible(true);
      scrollRef.current?.scrollTo({ top: 0 });
    }, 140);
  };

  // ── Theme tokens ─────────────────────────────────────────────────────────────
  const L = lightTheme;

  return (
    <div className={cn('flex flex-col h-full overflow-hidden', L ? 'bg-white' : 'bg-[#0d0d10]')}>

      {/* Header */}
      <div className={cn(
        'flex items-center gap-3 px-5 py-4 flex-shrink-0 border-b',
        L ? 'bg-white border-gray-200' : 'bg-gray-950 border-gray-800'
      )}>
        {onBack && (
          <button
            onClick={onBack}
            className={cn(
              'p-1.5 rounded-md transition-colors',
              L ? 'text-gray-400 hover:text-gray-900 hover:bg-gray-100' : 'text-gray-500 hover:text-white hover:bg-gray-800'
            )}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        )}

        <div className="flex-1 min-w-0">
          <h2 className={cn('font-semibold text-sm', L ? 'text-gray-900' : 'text-white')}>
            Avisos e Atualizações
          </h2>
          <p className={cn('text-xs mt-0.5', L ? 'text-gray-400' : 'text-gray-500')}>
            Panther Access · histórico de versões
          </p>
        </div>

        <div className="flex items-center gap-2">
          <span className={cn(
            'text-[11px] font-mono px-2 py-0.5 rounded border',
            L ? 'bg-gray-50 text-gray-500 border-gray-200' : 'bg-gray-900 text-gray-400 border-gray-800'
          )}>
            v{changelog[0].version}
          </span>
          <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-[#96bf13]/10 text-[#96bf13] border border-[#96bf13]/25">
            Atual
          </span>
        </div>
      </div>

      {/* Version tabs */}
      <div className={cn(
        'flex-shrink-0 overflow-x-auto border-b',
        L ? 'bg-gray-50 border-gray-200' : 'bg-gray-900/40 border-gray-800'
      )}>
        <div className="flex items-center gap-0.5 px-4 py-2 min-w-max">
          {changelog.map((r) => {
            const active = r.version === selectedVersion;
            const isLatest = r.version === changelog[0].version;
            return (
              <button
                key={r.version}
                onClick={() => switchVersion(r.version)}
                className={cn(
                  'flex items-center gap-2 px-3 py-1.5 rounded-md text-[11px] font-medium transition-all duration-150 whitespace-nowrap',
                  active
                    ? L
                      ? 'bg-gray-900 text-white'
                      : 'bg-white text-gray-900'
                    : L
                      ? 'text-gray-500 hover:text-gray-900 hover:bg-gray-200/60'
                      : 'text-gray-500 hover:text-gray-200 hover:bg-gray-800'
                )}
              >
                {isLatest && (
                  <span className="h-1.5 w-1.5 rounded-full bg-[#96bf13] flex-shrink-0" />
                )}
                <span>v{r.version}</span>
                <span className={cn(
                  'text-[10px] font-normal',
                  active ? L ? 'text-gray-400' : 'text-gray-500' : 'text-gray-500'
                )}>
                  {fmtShort(r.date)}
                </span>
                <span className={cn(
                  'h-4 min-w-[18px] px-1 rounded text-[9px] font-semibold flex items-center justify-center tabular-nums',
                  active
                    ? L ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-700'
                    : L ? 'bg-gray-200 text-gray-500' : 'bg-gray-800 text-gray-500'
                )}>
                  {r.entries.length}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Category filter */}
      <div className={cn(
        'flex-shrink-0 flex items-center gap-1.5 px-4 py-2.5 border-b overflow-x-auto',
        L ? 'border-gray-200' : 'border-gray-800'
      )}>
        {FILTERS.map(({ key, label }) => {
          const active = filter === key;
          const count = countFor(key);
          if (count === 0 && key !== 'todas') return null;

          let cls: string;
          if (active) {
            cls = key === 'todas'
              ? L ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-900 border-white'
              : CAT[key as ChangelogCategory].filterActive;
          } else {
            cls = L
              ? 'text-gray-500 border-gray-200 hover:border-gray-300 hover:text-gray-700'
              : 'text-gray-500 border-gray-800 hover:border-gray-700 hover:text-gray-300';
          }

          return (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={cn(
                'inline-flex items-center gap-1.5 px-2.5 py-1 rounded border text-[11px] font-medium transition-all duration-150 whitespace-nowrap',
                cls
              )}
            >
              {label}
              <span className={cn(
                'tabular-nums text-[10px]',
                active ? 'opacity-70' : L ? 'text-gray-400' : 'text-gray-600'
              )}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div
        ref={scrollRef}
        className={cn('flex-1 overflow-y-auto', !L && 'custom-scrollbar')}
      >
        {/* Announcements section */}
        {announcements.length > 0 && (
          <div className={cn('px-4 pt-4 pb-2 border-b', L ? 'border-gray-200' : 'border-gray-800')}>
            <p className={cn('text-[10px] font-semibold uppercase tracking-wider mb-2', L ? 'text-gray-400' : 'text-gray-600')}>
              Avisos
            </p>
            <div className="space-y-2">
              {announcements.map(ann => {
                const meta = CAT[ann.category] ?? CAT.aviso;
                return (
                  <div
                    key={ann.id}
                    className={cn(
                      'border border-l-[3px] rounded-lg px-3.5 py-3 transition-colors duration-150',
                      L ? meta.borderLight : meta.borderDark,
                      L
                        ? 'bg-white border-gray-100'
                        : 'bg-gray-900/40 border-gray-800/70'
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap mb-1">
                          <p className={cn('text-[10px] font-semibold uppercase tracking-wider', L ? meta.textLight : meta.textDark)}>
                            {meta.label}
                          </p>
                          {ann.pinned && (
                            <span className={cn('flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full', L ? 'bg-orange-50 text-orange-600' : 'bg-orange-900/30 text-orange-400')}>
                              <Pin className="h-2 w-2" /> Fixado
                            </span>
                          )}
                        </div>
                        <p className={cn('font-semibold text-sm mb-0.5', L ? 'text-gray-900' : 'text-gray-100')}>
                          {ann.title}
                        </p>
                        {ann.body && (
                          <p className={cn('text-xs leading-relaxed', L ? 'text-gray-500' : 'text-gray-500')}>
                            {ann.body}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className={cn(
          'transition-all duration-[140ms]',
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
        )}>
          {entries.length > 0 ? (
            <div className="p-4 space-y-2">
              {/* Date */}
              <p className={cn('text-[11px] font-medium mb-3', L ? 'text-gray-400' : 'text-gray-600')}>
                {fmtLong(release.date)}
              </p>

              {entries.map((entry) => {
                const meta = CAT[entry.category];
                return (
                  <div
                    key={entry.id}
                    className={cn(
                      'border border-l-[3px] rounded-lg px-4 py-3.5 transition-colors duration-150',
                      L ? meta.borderLight : meta.borderDark,
                      L
                        ? 'bg-white border-gray-100 hover:bg-gray-50'
                        : 'bg-gray-900/40 border-gray-800/70 hover:bg-gray-800/40'
                    )}
                  >
                    <p className={cn(
                      'text-[10px] font-semibold uppercase tracking-wider mb-1.5',
                      L ? meta.textLight : meta.textDark
                    )}>
                      {meta.label}
                    </p>
                    <p className={cn('font-semibold text-sm mb-1', L ? 'text-gray-900' : 'text-gray-100')}>
                      {entry.title}
                    </p>
                    <p className={cn('text-xs leading-relaxed', L ? 'text-gray-500' : 'text-gray-500')}>
                      {entry.description}
                    </p>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={cn(
              'flex items-center justify-center py-16 text-sm',
              L ? 'text-gray-400' : 'text-gray-600'
            )}>
              Nenhuma entrada nesta categoria
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangelogViewer;
